/**
 * Auth state management — login redirect and /me polling.
 * SDD §7.3 — lib/auth.js
 */

import { apiFetch, ApiError } from './api';

export interface AuthUser {
  member_id: string;
  azure_oid: string;
  email: string;
  name: string;
  role: string;
  rolePriority: number;
  battalion: { id: number; name: string } | null;
  assignment: { unit_id: string; seat_id: string; battalion_id: number } | null;
}

/** Returns true if the user's role has officer-level (priority >= 50) access or above. */
export function isOfficerOrAbove(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  // Check priority first, then fall back to role name for resilience against
  // misconfigured role definitions or missing rolePriority in the API response.
  return user.rolePriority >= 50 || user.role === 'officer' || user.role === 'admin';
}

/** Returns true if the user has the admin role. */
export function isAdmin(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'admin';
}

/**
 * Fetch the current authenticated user from /api/auth/me.
 * Returns null if not authenticated (401).
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    return await apiFetch<AuthUser>('/api/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

/**
 * Redirect the browser to the Azure AD login flow.
 */
export function redirectToLogin(): void {
  window.location.href = '/api/auth/login';
}

/**
 * POST to logout endpoint to clear session, then redirect.
 */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Even if the request fails, redirect to login
  }
  window.location.href = '/login';
}
